import { c as _c } from "react/compiler-runtime";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { fetchProjectIElements } from "@store/i-elements/i-elements-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCallback, useEffect, useRef } from "react";
import { markupsIElementsFetchingStatusSelector } from "@store/sdb-i-elements/sdb-i-elements-selectors";
import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
/**
 * Custom hook that takes care of fetching all markup elements and other associated elements
 * and storing them in the redux store. The markups are fetched only once per project, so that
 * every project tab can access them without fetching them again.
 * Use `markupsSelector` to read the fetched markups.
 *
 * Since a project can have thousands of elements, fetching all of them is inefficient.
 * We will fetch only the elements we need to keep the store as lightweight as possible.
 */
export function useFetchProjectMarkups(t0) {
  const $ = _c(14);
  const {
    projectId,
    shouldFetch
  } = t0;
  const dispatch = useAppDispatch();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const projectApiClient = useProjectApiClient(t1);
  const markupsIElementsFetchingStatus = useAppSelector(markupsIElementsFetchingStatusSelector);
  const fetchRequestRef = useRef(undefined);
  let t2;
  if ($[2] !== projectApiClient) {
    t2 = async signal => {
      const templatesContainer = await projectApiClient.getAllIElements({
        type: IElementType.group,
        typeHints: [IElementTypeHint.markup],
        signal
      });
      const ancestorIds = templatesContainer.map(_temp);
      const templates = await projectApiClient.getAllIElements({
        ancestorIds,
        signal
      });
      return templates;
    };
    $[2] = projectApiClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fetchMarkupTemplates = t2;
  let t3;
  let t4;
  if ($[4] !== dispatch || $[5] !== fetchMarkupTemplates || $[6] !== markupsIElementsFetchingStatus || $[7] !== projectApiClient || $[8] !== shouldFetch) {
    t3 = () => {
      if (!shouldFetch || markupsIElementsFetchingStatus !== "uninitialized") {
        return;
      }
      fetchRequestRef.current = dispatch(fetchProjectIElements({
        types: [IElementType.markup],
        fetcher: async signal_0 => {
          const markups = await projectApiClient.getAllIElements({
            type: IElementType.markup,
            signal: signal_0
          });
          if (!markups.length || signal_0.aborted) {
            return [];
          }
          const markupIds = markups.map(_temp2);
          const [children, templates_0, root] = await Promise.all([projectApiClient.getAllIElements({
            ancestorIds: markupIds,
            typeHints: [IElementTypeHint.markupAssigneeId, IElementTypeHint.markupIssueDueDate, IElementTypeHint.markupIssueStatus, IElementTypeHint.markupIssueImage, IElementTypeHint.attachments],
            signal: signal_0
          }), fetchMarkupTemplates(signal_0), projectApiClient.getRootIElement()]);
          return [...markups, ...children, ...templates_0, root];
        }
      }));
    };
    t4 = [dispatch, fetchMarkupTemplates, markupsIElementsFetchingStatus, projectApiClient, shouldFetch];
    $[4] = dispatch;
    $[5] = fetchMarkupTemplates;
    $[6] = markupsIElementsFetchingStatus;
    $[7] = projectApiClient;
    $[8] = shouldFetch;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => {
      fetchRequestRef.current?.abort();
      fetchRequestRef.current = undefined;
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== projectId) {
    t6 = [projectId];
    $[12] = projectId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t5, t6);
}
function _temp2(markup) {
  return markup.id;
}
function _temp(element) {
  return element.id;
}